/**
 * API client for board canvas persistence.
 *
 * Canvas interactions (drag, resize, restyle) fire far more often than they need
 * to be saved, so layout writes are coalesced through a debounced queue: callers
 * mark items dirty and a single PATCH flushes them together.
 */
import { jsonHeaders } from './apiHelpers';
import type { BoardItem, BoardItemKind, BoardItemStyle, BoardViewport, Project } from './types';

/** Shape of a single item in a layout PATCH payload. */
export interface BoardLayoutPatch {
  id: string;
  x: number;
  y: number;
  width?: number | null;
  height?: number | null;
  zIndex?: number;
  style?: BoardItemStyle;
  content?: string | null;
}

const LAYOUT_DEBOUNCE_MS = 600;
const LAYOUT_RETRY_MS = 5_000;

export interface CreateBoardItemInput {
  kind: BoardItemKind;
  x: number;
  y: number;
  width?: number;
  height?: number;
  projectId?: string;
  content?: string;
  style?: BoardItemStyle;
}

export async function createBoardItem(
  boardId: string,
  input: CreateBoardItemInput,
): Promise<{ item: BoardItem; project: Project | null }> {
  const response = await fetch(`/boards/${boardId}/items`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Could not add item to board');
  }

  return response.json();
}

export interface BulkCreateBoardItemsResponse {
  created: { item: BoardItem; project: Project }[];
  skipped: { projectId: string; reason: string }[];
}

export async function createBoardItems(
  boardId: string,
  inputs: CreateBoardItemInput[],
): Promise<BulkCreateBoardItemsResponse> {
  const response = await fetch(`/boards/${boardId}/items/bulk`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ items: inputs }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok && !body.created) throw new Error(body.error || 'Could not add items to board');
  return body;
}

export async function deleteBoardItems(boardId: string, itemIds: string[]): Promise<string[]> {
  const response = await fetch(`/boards/${boardId}/items`, {
    method: 'DELETE',
    headers: jsonHeaders(),
    body: JSON.stringify({ itemIds }),
  });
  if (!response.ok) throw new Error('Could not remove items from board');
  return (await response.json()).discardedIds;
}

export async function restoreBoardItems(
  boardId: string,
  itemIds: string[],
): Promise<{
  items: BoardItem[];
  projects: Project[];
  skipped: { id: string; reason: string }[];
}> {
  const response = await fetch(`/boards/${boardId}/items/restore`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ itemIds }),
  });
  if (!response.ok) throw new Error('Could not restore items to board');
  return response.json();
}

export async function deleteBoardItem(boardId: string, itemId: string): Promise<void> {
  const response = await fetch(`/boards/${boardId}/items/${itemId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });

  if (!response.ok) {
    throw new Error('Could not remove item from board');
  }
}

/**
 * Debounced writer for positions, sizes, styles and viewport.
 *
 * One instance per open board. Repeated calls to `queueItems` merge by item id,
 * so dragging a card across the canvas results in a single write of its final
 * resting position rather than one per animation frame.
 */
export function createLayoutPersister(
  boardId: string,
  options: { onError?: (error: Error) => void; debounceMs?: number } = {},
) {
  const debounceMs = options.debounceMs ?? LAYOUT_DEBOUNCE_MS;

  let pendingItems = new Map<string, BoardLayoutPatch>();
  let pendingViewport: BoardViewport | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let flushAfterFlight = false;

  function restoreFailedPayload(payload: { items: BoardLayoutPatch[]; viewport?: BoardViewport }) {
    // Preserve newer edits queued while this request was in flight.
    for (const patch of payload.items) {
      pendingItems.set(patch.id, { ...patch, ...pendingItems.get(patch.id) });
    }
    pendingViewport ??= payload.viewport ?? null;
  }

  function send(): Promise<void> {
    timer = null;

    // Never overlap writes: otherwise a slow older request can overwrite a newer
    // drag/resize request after it reaches the server.
    if (inFlight) return inFlight;
    if (pendingItems.size === 0 && !pendingViewport) return Promise.resolve();

    const payload: { items: BoardLayoutPatch[]; viewport?: BoardViewport } = {
      items: [...pendingItems.values()],
    };
    if (pendingViewport) payload.viewport = pendingViewport;

    // Detach this batch before awaiting so edits made during the request form the
    // next batch. On failure the detached batch is merged back below.
    pendingItems = new Map();
    pendingViewport = null;

    inFlight = (async () => {
      let failed = false;
      try {
        const response = await fetch(`/boards/${boardId}/layout`, {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`Layout save failed (${response.status})`);
      } catch (error) {
        failed = true;
        restoreFailedPayload(payload);
        options.onError?.(error instanceof Error ? error : new Error('Layout save failed'));
      } finally {
        inFlight = null;
        const shouldFlush = flushAfterFlight;
        flushAfterFlight = false;
        if (pendingItems.size > 0 || pendingViewport) {
          if (shouldFlush) {
            void send();
          } else {
            schedule(failed ? LAYOUT_RETRY_MS : debounceMs);
          }
        }
      }
    })();

    return inFlight;
  }

  function schedule(delay = debounceMs) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void send(), delay);
  }

  return {
    queueItems(patches: BoardLayoutPatch[]) {
      for (const patch of patches) {
        pendingItems.set(patch.id, { ...pendingItems.get(patch.id), ...patch });
      }
      schedule();
    },

    queueViewport(viewport: BoardViewport) {
      pendingViewport = viewport;
      schedule();
    },

    /** Write immediately — used on unmount and page hide so nothing is lost. */
    async flush() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (inFlight) {
        flushAfterFlight = true;
        await inFlight;
      }
      return send();
    },

    get hasPending() {
      return pendingItems.size > 0 || pendingViewport !== null || inFlight !== null;
    },
  };
}

export type LayoutPersister = ReturnType<typeof createLayoutPersister>;
